import './Projects.css';
import { useEffect, useState } from 'react';

import sumoImg from './IMAGE/SUMO.jpg';
import ambientImg from './IMAGE/AMBIENT.jpeg';
import voiceImg from './IMAGE/VOICE CONTROLLED.jpeg';

import miyabiImg from './IMAGE/MIYABI.png';
import inventoryImg from './IMAGE/INVENTORY.jpeg';
import smartAccessImg from './IMAGE/SMART ACCESS.jpeg';
import heatImg from './IMAGE/HEAT.png';
import pcbImg from './IMAGE/PCB.jpeg';
import pacImg from './IMAGE/PAC.jpeg';
import snakeImg from './IMAGE/SNAKE.jpeg';

function Projects({ onNavigateHome }) {
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const projects = [
    {
      title: "Pacman <em>Game</em> on FPGA",
      desc: "A complete hardware implementation of the classic Pacman arcade game on an FPGA board, featuring custom game logic, state machines, and VGA display output.",
      tools: ["Verilog", "FPGA", "VGA Interface", "Digital Logic"],
      role: "Hardware Developer",
      course: "CPE-MIC",
      image: pacImg
    },
    {
      title: "Ambient Light-Based Smart Lighting System",
      desc: "An automated lighting control system that adjusts room brightness based on natural ambient light levels using LDR sensors and PID control.",
      tools: ["ESP32", "Control Systems", "LDR Sensors", "C++"],
      role: "Control Systems Engineer",
      course: "FEEDBACK AND CONTROL SYSTEM",
      image: ambientImg
    },
    {
      title: "MicroLab <em>Inventory</em> Management System",
      desc: "A comprehensive lab inventory system for tracking electronic components, microcontrollers, and tools — with checkout and restock capabilities.",
      tools: ["React", "Node.js", "Firebase", "CSS"],
      role: "Full-Stack Developer",
      course: "CPE-OOP",
      image: inventoryImg
    },
    {
      title: "Two-Digit Decimal Counter PCB Design",
      desc: "Custom printed circuit board layout and schematic for a hardware two-digit 7-segment display counter utilizing 555 timers and logic gates.",
      tools: ["KiCad", "Circuit Design", "Logic Gates"],
      role: "Hardware Designer",
      course: "CPE-DES",
      image: pcbImg
    },
    {
      title: "Voice-Controlled LED System",
      desc: "An embedded system project utilizing an ESP32 microcontroller and Fast Fourier Transform (FFT) to process voice commands and control lighting patterns.",
      tools: ["ESP32", "C++", "FFT", "Audio Processing"],
      role: "Embedded Systems Developer",
      course: "ECE-DSP",
      image: voiceImg
    },
    {
      title: "Restaurant Web App",
      desc: "A responsive front-end web application for a restaurant featuring online menus, reservation forms, and interactive UI components.",
      tools: ["HTML", "CSS", "JavaScript", "React"],
      role: "Front-End Developer",
      course: "sOFTWARE DESIGN",
      image: miyabiImg
    },
    {
      title: "Smart Room Access & Status Indication",
      desc: "An RFID-based room access control system integrated with LCD status indicators and automated logging.",
      tools: ["Arduino", "RFID", "C++", "Sensors"],
      role: "Embedded Developer",
      course: "EMBEDDED SYSTEM",
      image: smartAccessImg
    },
    {
      title: "Sumo Bot",
      desc: "An autonomous, combat-ready robot built to locate and push opponents out of a ring using ultrasonic and line-tracking sensors.",
      tools: ["Arduino", "Motor Drivers", "Sensors", "C++"],
      role: "Robotics Programmer",
      course: "EMBEDDED SYSTEM",
      image: sumoImg
    },
    {
      title: "3D Heat Conduction on Metal Plates",
      desc: "A simulation tool solving Partial Differential Equations (PDE) to visualize 3D heat distribution over time across various metal plate materials.",
      tools: ["Numerical Methods", "PDEs"],
      role: "Simulation Engineer",
      course: "CPE-MATH",
      image: heatImg
    },
    {
      title: "Snake & <em>Ladder</em> Game",
      desc: "A fully functional digital version of the classic board game, featuring player vs player or player vs CPU modes, complete with sound effects and animations.",
      tools: ["Python", "Pygame", "Object-Oriented Programming"],
      role: "Lead Developer",
      course: "CPE-CP1",
      image: snakeImg
    },
    {
      title: "Graduate Tracer Study of CPE Graduates",
      desc: "A research project and accompanying web dashboard analyzing the employment statistics and curriculum relevance for recent Computer Engineering graduates.",
      tools: ["Data Analysis", "Python", "Pandas", "Chart.js"],
      role: "Data Analyst & Researcher",
      course: "RESEARCH"
    },
    {
      title: "Secure Digital Lock System",
      desc: "An FPGA-based digital lock system designed using Verilog with Authentication by Verification (ABV) concepts for enhanced hardware security.",
      tools: ["Verilog", "FPGA", "Digital Logic"],
      role: "Hardware Verification Engineer",
      course: "CPE-HDL"
    }
  ];

  return (
    <div className="proj-page">
      {/* Fixed Widescreen 3D Ambient Orbs Background */}
      <div className="page-orb-container">
        <div className="page-orb page-orb-left"></div>
        <div className="page-orb page-orb-right"></div>
        <div className="page-orb page-orb-bottom"></div>
      </div>

      {/* ── Header ── (removed for global navbar) */}

      {/* ── Hero Strip ── */}
      <div className="proj-hero-strip">
        <div className="proj-strip-inner">
          <p className="proj-strip-label">Technical Outputs</p>
          <h1 className="proj-strip-name">Projects</h1>
          <p className="proj-strip-role">A collection of my academic, embedded, and software development work.</p>
          <div className="hero-stats-row">
            <span className="hero-stat-pill">▸ 12 Completed Projects</span>
            <span className="hero-stat-pill">▸ Full-Stack & Embedded</span>
            <span className="hero-stat-pill">▸ Hardware / FPGA</span>
          </div>
          <div className="hero-watermark">⬡</div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <main className="proj-main">
        <div className="proj-grid">
          {projects.map((proj, idx) => (
            <div 
              className={`proj-card${proj.image ? ' has-image' : ''}`} 
              key={idx}
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              {proj.image && (
                <div className="proj-card-image" onClick={() => setSelectedImage(proj.image)}>
                  <img
                    src={proj.image}
                    alt={proj.title.replace(/<[^>]*>/g, '')}
                  />
                  <div className="proj-card-zoom-overlay">
                    <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      <line x1="11" y1="8" x2="11" y2="14"></line>
                      <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                  </div>
                </div>
              )}
              {!proj.image && (
                <div className="proj-card-image proj-card-image-placeholder" aria-hidden="true"></div>
              )}
              <div className="proj-card-content">
                <div className="proj-header-row">
                  <div className="proj-icon-box">
                    {proj.course && (proj.course.includes('MIC') || proj.course.includes('HDL') || proj.course.includes('DES')) ? '🔌' : 
                     proj.course && (proj.course.includes('SYSTEM') || proj.course.includes('FEEDBACK')) ? '💡' :
                     proj.course && (proj.course.includes('MATH') || proj.course.includes('DSP')) ? '🧠' :
                     proj.course && (proj.course.includes('SOFTWARE') || proj.course.includes('OOP')) ? '🌐' : '📂'}
                  </div>
                  <span className="proj-course">{proj.course}</span>
                </div>
                <div className="proj-text">
                  <h3
                    className="proj-title"
                    dangerouslySetInnerHTML={{ __html: proj.title }}
                  />
                  <div className="proj-divider"></div>
                  <p className="proj-desc">{proj.desc}</p>
                </div>

                <div className="proj-tools-section">
                  <span className="proj-tools-label">BUILT WITH</span>
                  <div className="proj-tools">
                    {proj.tools.map((tool, tIdx) => (
                      <span className="proj-tool-tag" key={tIdx}>{tool}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="proj-footer">
        <p>Christian D. Kalaw &copy; {new Date().getFullYear()}</p>
        <button className="proj-footer-back" onClick={onNavigateHome}>Back to Portfolio</button>
      </footer>

      {/* ── Image Lightbox Modal ── */}
      {selectedImage && (
        <div className="proj-lightbox" onClick={() => setSelectedImage(null)}>
          <div className="proj-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="proj-lightbox-close" onClick={() => setSelectedImage(null)}>×</button>
            <img src={selectedImage} alt="Enlarged project view" className="proj-lightbox-img" />
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;
